import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { RailWallFixing } from "../../../../../http/border/types/railWallFixings";
import { useRailWallFixingsApi } from "../../../../../http/border/useRailWallFixings";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateRailWallFixingModalDialogButton from "./CreateRailWallFixingModalDialogButton";
import RailWallFixingForm from "./RailWallFixingForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const RailWallFixings: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "railWallFixings" });
  const aborter = useRef(new AbortController());
  const railWallFixingsApi = useRailWallFixingsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "railWallFixings");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await railWallFixingsApi.updateAdminRailWallFixingVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await railWallFixingsApi.updateAdminRailWallFixingOrder(id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToDelete, setItemToDelete] = useState<RailWallFixing>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("border.rail_wall_fixing");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = railWallFixingsApi.useAdminRailWallFixings(query, aborter.current.signal);

  const [itemToEdit, setItemToEdit] = useState<RailWallFixing>();

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.railWallFixings.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.railWallFixings.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateRailWallFixingModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<RailWallFixing>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="left">{i18n.translation.rails.plural}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{item.item.code}</td>
            <td>{item.item.description}</td>
            <td>{item.rails.map((rail) => rail.item.code).join("; ")}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item.item),
          getDeleteAction(i18n, () => setItemToDelete(item), false),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.railWallFixings.edit}>
          <RailWallFixingForm onClose={() => setItemToEdit(undefined)} railWallFixing={itemToEdit} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.railWallFixings.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                railWallFixingsApi
                  .deleteAdminRailWallFixing(itemToDelete)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.railWallFixings.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default RailWallFixings;
